import React, { useEffect, useState, useRef } from "react";
import { useSelector, useStore } from "react-redux";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import SelectionDropdown from "../SelectionDropdown";
import Spinner from "../Loader/Spinner";
import ProgressBar from "../Loader/ProgressBar";
import "../CHMPage/CHMPage.css";
import "../CropwiseData/CropwiseData.css";
import { prettyName, canonicalName, extractProp } from "../../utils/name";
import { GEO_SERVER_BASE_URL } from "../../config/constants";

// Loading state constants
const LOADING_STATES = {
  IDLE: 'idle',
  LOCATION_SEARCH: 'location_search',
  CROP_LOADING: 'crop_loading',
  KHASRA_LOADING: 'khasra_loading'
};

/* ---------- safe fitBounds wrapper ---------- */
const safeFitBounds = (map, layer) => {
  if (!map || !layer) return;
  try {
    const bounds = layer.getBounds();
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    } else {
      console.warn("[CHMPage] safeFitBounds: invalid bounds, skipping.");
    }
  } catch (err) {
    console.warn("[CHMPage] safeFitBounds error:", err);
  }
};

const CropMappingView = () => {
  const mapRef = useRef(null);
  let currentPolygonLayer = null;

  // Loading states
  const [loadingState, setLoadingState] = useState(LOADING_STATES.IDLE);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');

  const mapInstanceRef = useRef(null);
  const khasraHighlightRef = useRef(null);
  const hasShownKhasraErrorRef = useRef(false);
  const hasShownKhasraTileErrorRef = useRef(false);

  const {
    state: reduxState,
    district: reduxDistrict,
    tehsil: reduxTehsil,
    village: reduxVillage
  } = useSelector((state) => state.location);
  const { geojson } = useSelector((state) => state.geo);
  const store = useStore(); // Direct store access for Condition 2


  // Location states
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTehsil, setSelectedTehsil] = useState("");
  const [selectedVillage, setSelectedVillage] = useState("");

  // Layer states
  const [isSatelliteVisible, setIsSatelliteVisible] = useState(true);
  const satelliteLayerRef = useRef(null);
  const [showKhasra, setShowKhasra] = useState(false);
  const khasraLayerRef = useRef(null);
  const [geoJsonLayer, setGeoJsonLayer] = useState(null);
  const [stateLayer, setStateLayer] = useState(null);
  const [districtLayer, setDistrictLayer] = useState(null);
  const [tehsilLayer, setTehsilLayer] = useState(null);

  // Crop states
  const [selectedCrop, setSelectedCrop] = useState("");
  const [cropData, setCropData] = useState([]);
  const [areLayersHidden, setAreLayersHidden] = useState(false);
  const [geoJsonReady, setGeoJsonReady] = useState(false);
  const [cropLayerLoading, setCropLayerLoading] = useState(false);


  // Initialize map
  useEffect(() => {
    if (!mapInstanceRef.current) {
      const map = L.map(mapRef.current).setView([20, 78], 5);
      mapInstanceRef.current = map;

      const googleSatelliteLayer = L.tileLayer(
        "http://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
        {
          maxZoom: 20,
          subdomains: "0123",
          attribution: "Map data &copy; Google",
        }
      );
      satelliteLayerRef.current = googleSatelliteLayer;
      googleSatelliteLayer.addTo(map);

      // ✅ Condition 2: After map loads, wait for cached thunks to resolve, then zoom
      setTimeout(() => {
        const { location, geo } = store.getState();
        if (location.village && geo.geojson.village && mapInstanceRef.current) {
          console.log("🔄 [Condition 2] Zooming to:", location.village);
          handleSearch(location.state, location.district, location.tehsil, location.village, geo.geojson.village);
        }
      }, 300);
    }
  }, []);

  // Get loading message based on state
  const getLoadingMessage = (state, progress) => {
    switch (state) {
      case LOADING_STATES.LOCATION_SEARCH:
        if (progress < 40) return "Searching location...";
        if (progress < 80) return "Loading boundaries...";
        return "Finalizing map view...";
      case LOADING_STATES.CROP_LOADING:
        if (progress < 30) return "Fetching crop data...";
        if (progress < 70) return "Processing crop layer...";
        return "Rendering crop overlay...";
      case LOADING_STATES.KHASRA_LOADING:
        if (progress < 50) return "Loading Khasra data...";
        return "Preparing field boundaries...";
      default:
        return "Loading...";
    }
  };

  const handleSearch = (state, district, tehsil, village, geoJsonData) => {
    try {
      setGeoJsonReady(false);
      setLoadingState(LOADING_STATES.LOCATION_SEARCH);
      setProgress(10);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LOCATION_SEARCH, 10));

      setSelectedState(state);
      setSelectedDistrict(district);
      setSelectedTehsil(tehsil);
      setSelectedVillage(village);

      if (!geoJsonData || !geoJsonData.features) {
        console.warn("Invalid or missing GeoJSON data.");
        return;
      }

      setProgress(30);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LOCATION_SEARCH, 30));

      // Use local variables to track layers within this function execution
      let currentDistrictLayer = districtLayer;
      let currentTehsilLayer = tehsilLayer;

      // Store district layer without removing it
      if (district) {
        // Define helper at consistent scope
        const getDistrictName = (feature) =>
          feature.properties.district ||
          feature.properties.District ||
          feature.properties.NAME_2;

        let targetLayer = currentDistrictLayer;

        if (!targetLayer) {
          const newDistrictLayer = L.geoJSON(geoJsonData, {
            style: function (feature) {
              const featDistrict = getDistrictName(feature);
              const isSelected = featDistrict === district;
              return {
                color: "red",
                weight: 2,
                fillOpacity: 0,
              };
            },
            onEachFeature: function (feature, layer) {
              const districtName = feature.properties.District || feature.properties.district || feature.properties.NAME_2;
              setTimeout(() => {
                const el = layer.getElement();
                if (el) el.style.pointerEvents = "none";
              }, 100);
              layer.bindTooltip(districtName, {
                permanent: true,
                direction: "center",
                className: "district-label",
                opacity: tehsil ? 0 : 1 // ✅ Hide text if Tehsil is selected
              });

              if (tehsil) {
                layer.closeTooltip();
                layer.unbindTooltip();
              } else {
                layer.bindTooltip(districtName, {
                  permanent: true,
                  direction: "center",
                  className: "district-label",
                });
              }
              if (districtName === district) {
                layer
                  .bindPopup(`<b>Selected District:</b> ${districtName}`)
                  .openPopup();
              }
            },
          }).addTo(mapInstanceRef.current);

          setDistrictLayer(newDistrictLayer);
          targetLayer = newDistrictLayer;
          currentDistrictLayer = newDistrictLayer; // Update local ref
        } else {
          targetLayer.setStyle({ opacity: 1 });
          if (!mapInstanceRef.current.hasLayer(targetLayer)) {
            targetLayer.addTo(mapInstanceRef.current);
          }
        }

        // ✅ REFACTORED ZOOM LOGIC: Runs ALWAYS when district is selected
        const allLayers = targetLayer.getLayers();

        const selectedDistrictLayer = allLayers.find((l) => {
          const n = getDistrictName(l.feature);
          return canonicalName(n) === canonicalName(district);
        });

        if (selectedDistrictLayer) {
          mapInstanceRef.current.fitBounds(selectedDistrictLayer.getBounds(), { padding: [30, 30] });
        } else {
          mapInstanceRef.current.fitBounds(targetLayer.getBounds());
        }
      } else if (state && !district) {
        // ✅ State-only view: Render all districts with Labels and State highlight
        if (currentDistrictLayer) {
          mapInstanceRef.current.removeLayer(currentDistrictLayer);
        }
        if (tehsilLayer) {
          mapInstanceRef.current.removeLayer(tehsilLayer);
          setTehsilLayer(null);
        }
        if (geoJsonLayer) {
          mapInstanceRef.current.removeLayer(geoJsonLayer);
          setGeoJsonLayer(null);
        }

        const newStateLayer = L.geoJSON(geoJsonData, {
          style: {
            color: "red",
            weight: 2,
            fillOpacity: 0,
          },
          onEachFeature: function (feature, layer) {
            const districtName = feature.properties.District || feature.properties.district || feature.properties.NAME_2;
            layer.bindTooltip(districtName, {
              permanent: true,
              direction: "center",
              className: "district-label",
            });
            setTimeout(() => {
              const el = layer.getElement();
              if (el) el.style.pointerEvents = "none";
            }, 100);
          }
        }).addTo(mapInstanceRef.current);

        setDistrictLayer(newStateLayer);
        currentDistrictLayer = newStateLayer;
        mapInstanceRef.current.fitBounds(newStateLayer.getBounds());
      }

      setProgress(60);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LOCATION_SEARCH, 60));

      // Store tehsil layer without replacing district
      if (tehsil) {
        setSelectedTehsil(tehsil);
        const getTehsilName = (feature) =>
          feature.properties?.tehsil ||
          feature.properties?.subdist ||
          feature.properties?.NAME_3;

        const normalizedTehsil = tehsil?.toLowerCase().trim();
        const normalizedDistrict = selectedDistrict?.toLowerCase().trim();

        // Filter features just for ZOOMING, but render ALL features
        const filteredTehsilFeatures = geoJsonData.features.filter(
          (feature) => {
            const districtName =
              feature.properties?.District ||
              feature.properties?.district ||
              feature.properties?.NAME_2;
            const tehsilName = getTehsilName(feature);

            return (
              districtName?.toLowerCase().trim() === normalizedDistrict &&
              tehsilName?.toLowerCase().trim() === normalizedTehsil
            );
          }
        );

        const allTehsilFeatures = geoJsonData.features || [];

        // ✅ Guard: Only render/zoom to Tehsil if NO village is selected (matches DEMView)
        if (allTehsilFeatures.length > 0 && !village) {
          if (currentTehsilLayer) {
            mapInstanceRef.current.removeLayer(currentTehsilLayer);
          }

          const newTehsilLayer = L.geoJSON(
            { ...geoJsonData, features: allTehsilFeatures },
            {
              // style: { color: "red", weight: 2, fillOpacity: 0 },
              style: function (feature) {
                const tehsilName = getTehsilName(feature);
                const isSelected = tehsilName?.toLowerCase().trim() === normalizedTehsil;

                return {
                  color: isSelected ? "#DC2626" : "#FB923C", // Red vs Orange
                  weight: isSelected ? 3 : 1,
                  fillOpacity: isSelected ? 0.1 : 0,
                  fillColor: isSelected ? "#DC2626" : "transparent"
                };
              },
              onEachFeature: function (feature, layer) {
                const tehsilName =
                  feature.properties.NAME_3 || feature.properties.tehsil;

                const isSelected = tehsilName?.toLowerCase().trim() === normalizedTehsil;

                layer.bindPopup(`<b>Tehsil:</b> ${tehsilName}`);
                layer.bindTooltip(tehsilName, {
                  permanent: true,
                  direction: "center",
                  className: isSelected ? "tehsil-label tehsil-label-selected" : "tehsil-label",
                });
                setTimeout(() => {
                  const el = layer.getElement();
                  if (el) el.style.pointerEvents = "none";
                }, 100);
              },
            }
          ).addTo(mapInstanceRef.current);

          setTehsilLayer(newTehsilLayer);
          currentTehsilLayer = newTehsilLayer;

          if (filteredTehsilFeatures.length > 0) {
            const tempLayer = L.geoJSON({
              type: "FeatureCollection",
              features: filteredTehsilFeatures,
            });
            mapInstanceRef.current.fitBounds(tempLayer.getBounds());
          } else {
            mapInstanceRef.current.fitBounds(newTehsilLayer.getBounds());
          }
        } else {
          if (currentTehsilLayer) {
            currentTehsilLayer.setStyle({ opacity: 1 });
          }
        }
      }

      setProgress(80);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LOCATION_SEARCH, 80));

      // ✅ Village layer handling matching DEMView.js & CHMPage.js
      if (village) {
        // Clean up previous village layer
        if (geoJsonLayer) {
          mapInstanceRef.current.removeLayer(geoJsonLayer);
        }

        const allFeatures = geoJsonData.features || [];

        if (allFeatures.length > 0) {
          const newVillageLayer = L.geoJSON(
            { type: "FeatureCollection", features: allFeatures },
            {
              style: function (feature) {
                const getVillageName = (f) => f.properties?.village || f.properties?.NAME_4 || f.properties?.NAME;
                const featVillage = (getVillageName(feature) || "").toLowerCase().trim();
                const selectedVillageName = (village || "").toLowerCase().trim();

                const isSelected = featVillage === selectedVillageName;
                return {
                  color: isSelected ? "#7C3AED" : "#F97316", // Violet for selected, Orange for others
                  weight: isSelected ? 3 : 1,
                  fillOpacity: isSelected ? 0.15 : 0,
                  fillColor: isSelected ? "#7C3AED" : "transparent",
                };
              },
              onEachFeature: function (feature, layer) {
                const getVillageName = (f) => f.properties?.village || f.properties?.NAME_4 || f.properties?.NAME;
                const villageRaw = getVillageName(feature);
                if (!villageRaw) return;

                const displayName = villageRaw;
                const featVillage = (villageRaw || "").toLowerCase().trim();
                const selectedVillageName = (village || "").toLowerCase().trim();
                const isSelected = featVillage === selectedVillageName;

                layer.bindPopup(`<b>Village:</b> ${displayName}`);
                layer.bindTooltip(displayName, {
                  permanent: true,
                  direction: "center",
                  className: isSelected
                    ? "village-label village-label-selected"
                    : "village-label",
                });

                setTimeout(() => {
                  const el = layer.getElement();
                  if (el) el.style.pointerEvents = "none";
                }, 100);
              },
            }
          ).addTo(mapInstanceRef.current);

          setGeoJsonLayer(newVillageLayer);

          // Zoom to the SELECTED village specifically
          const selectedVillageName = canonicalName(village);

          const selectedFeatures = allFeatures.filter((f) => {
            const rawName = extractProp(f.properties, "village", "Village", "NAME_4", "NAME", "Name");
            const fv = canonicalName(rawName);
            return fv === selectedVillageName;
          });

          if (selectedFeatures.length > 0) {
            const tempLayer = L.geoJSON({
              type: "FeatureCollection",
              features: selectedFeatures,
            });

            if (mapInstanceRef.current) {
              const bounds = tempLayer.getBounds();
              if (bounds && bounds.isValid()) {
                mapInstanceRef.current.fitBounds(bounds, {
                  padding: [20, 20],
                  maxZoom: 15
                });
              }
            }
          } else {
            if (mapInstanceRef.current) {
              safeFitBounds(mapInstanceRef.current, newVillageLayer);
            }
          }

          // ✅ Hide tehsil and district when village is selected by REMOVING them
          // Use local variables to ensure we remove the correct layers
          if (currentTehsilLayer && mapInstanceRef.current.hasLayer(currentTehsilLayer)) {
            mapInstanceRef.current.removeLayer(currentTehsilLayer);
          }
          if (currentDistrictLayer && mapInstanceRef.current.hasLayer(currentDistrictLayer)) {
            mapInstanceRef.current.removeLayer(currentDistrictLayer);
          }

        } else {
          // No features
          if (geoJsonLayer) {
            mapInstanceRef.current.removeLayer(geoJsonLayer);
            setGeoJsonLayer(null);
          }
          if (currentTehsilLayer && !mapInstanceRef.current.hasLayer(currentTehsilLayer)) {
            currentTehsilLayer.addTo(mapInstanceRef.current);
          }
        }

      } else {
        // No village selected
        if (geoJsonLayer) {
          mapInstanceRef.current.removeLayer(geoJsonLayer);
          setGeoJsonLayer(null);
        }

        if (currentTehsilLayer && !mapInstanceRef.current.hasLayer(currentTehsilLayer)) {
          currentTehsilLayer.addTo(mapInstanceRef.current);
        }
        if (currentTehsilLayer) {
          currentTehsilLayer.setStyle({ opacity: 1, fillOpacity: 0 });
          mapInstanceRef.current.fitBounds(currentTehsilLayer.getBounds());
        }

        if (currentDistrictLayer && !mapInstanceRef.current.hasLayer(currentDistrictLayer)) {
          currentDistrictLayer.addTo(mapInstanceRef.current);
        }
        if (currentDistrictLayer) currentDistrictLayer.setStyle({ opacity: 0.3 });
      }

      setProgress(100);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LOCATION_SEARCH, 100));
    } catch (err) {
      console.error("Error in handleSearch:", err);
    } finally {
      setTimeout(() => {
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');
      }, 400);
    }
  };

  const handleHideLayers = () => {
    if (areLayersHidden) {
      // Show all layers again
      if (districtLayer) districtLayer.setStyle({ opacity: 1 });
      if (tehsilLayer) tehsilLayer.setStyle({ opacity: 1 });
      if (geoJsonLayer) geoJsonLayer.setStyle({ opacity: 1 });
    } else {
      // Hide all layers
      if (districtLayer) districtLayer.setStyle({ opacity: 0 });
      if (tehsilLayer) tehsilLayer.setStyle({ opacity: 0 });
      if (geoJsonLayer) geoJsonLayer.setStyle({ opacity: 0 });
    }

    setAreLayersHidden(!areLayersHidden);
  };

  const fetchCropLayersFromGeoServer = () => {
    if (!selectedState) return;
    let cropNames = [];

    // Normalize village for safe comparison
    const normalizedVillage = selectedVillage
      ?.toLowerCase()
      .replace(/\s+/g, "_");

    console.log("📍 Village selected:", selectedVillage);
    console.log("✅ Normalized:", normalizedVillage);

    if (selectedState === "Rajasthan") {
      cropNames = ["Moong", "Bajra", "Groundnut"];

      // Special override for Jeevan Desa & Armalsar
      if (
        normalizedVillage === "jeewan_desa" ||
        normalizedVillage === "armalsar"
      ) {
        cropNames = ["Wheat", "Bengal_Gram", "Mustard"];
      }
    } else if (selectedState === "Maharashtra") {
      cropNames = ["Soyabean", "Cotton"];
    } else if (selectedState === "Uttar Pradesh") {
      cropNames = ["Mustard", "Wheat"];
    }

    console.log("🌾 Final Crop Dropdown:", cropNames);
    setCropData(cropNames);
  };

  useEffect(() => {
    fetchCropLayersFromGeoServer();
  }, [selectedState, selectedVillage]);

  const handleCropChange = async (e) => {
    const selectedCropName = e.target.value;
    setSelectedCrop(selectedCropName);

    if (!selectedState || !selectedCropName) {
      // Remove existing crop overlay if no crop is selected
      if (mapInstanceRef.current.cropOverlay) {
        mapInstanceRef.current.removeLayer(mapInstanceRef.current.cropOverlay);
        mapInstanceRef.current.cropOverlay = null;
      }
      return;
    }

    // Start crop loading
    setLoadingState(LOADING_STATES.CROP_LOADING);
    setCropLayerLoading(true);
    setProgress(10);
    setLoadingMessage(getLoadingMessage(LOADING_STATES.CROP_LOADING, 10));

    try {
      // Remove existing crop overlay if it exists
      if (mapInstanceRef.current.cropOverlay) {
        mapInstanceRef.current.removeLayer(mapInstanceRef.current.cropOverlay);
        mapInstanceRef.current.cropOverlay = null;
      }

      setProgress(30);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.CROP_LOADING, 30));

      const wmsUrl = `${GEO_SERVER_BASE_URL}/wms`;

      // Use village-specific naming if it's Jeevan_Desa or Armalsar
      let cropLayerPath;
      const normalizedVillage = selectedVillage?.toLowerCase().trim();

      if (
        normalizedVillage === "jeewan_desa" ||
        normalizedVillage === "armalsar"
      ) {
        cropLayerPath = `ne:${selectedVillage}_${selectedCropName}`;
      } else {
        cropLayerPath = `ne:${selectedState}_${selectedCropName}`;
      }

      setProgress(60);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.CROP_LOADING, 60));

      const cropLayer = L.tileLayer.wms(wmsUrl, {
        layers: cropLayerPath,
        format: "image/png",
        transparent: true,
        version: "1.1.0",
        attribution: "GeoServer",
      });

      // Add loading event handlers
      cropLayer.on('loading', () => {
        setProgress(70);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.CROP_LOADING, 70));
      });

      cropLayer.on('load', () => {
        setProgress(100);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.CROP_LOADING, 100));

        // Hide loading after a brief delay
        setTimeout(() => {
          setLoadingState(LOADING_STATES.IDLE);
          setCropLayerLoading(false);
          setProgress(0);
          setLoadingMessage('');
        }, 500);
      });

      cropLayer.on('tileerror', (error) => {
        console.error('Crop layer loading error:', error);
        setLoadingState(LOADING_STATES.IDLE);
        setCropLayerLoading(false);
        setProgress(0);
        setLoadingMessage('');
        alert('Failed to load crop layer. Please try again.');
      });

      cropLayer.addTo(mapInstanceRef.current);
      mapInstanceRef.current.cropOverlay = cropLayer;

      setProgress(90);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.CROP_LOADING, 90));

    } catch (error) {
      console.error('Error loading crop layer:', error);
      setLoadingState(LOADING_STATES.IDLE);
      setCropLayerLoading(false);
      setProgress(0);
      setLoadingMessage('');
      alert('Failed to load crop layer. Please check your connection and try again.');
    }
  };

  const setupKhasraClickHandler = (
    map,
    khasraLayer,
    villageName,
    selectedTehsil
  ) => {
    // ✅ Remove any existing click handlers first
    map.off("click");

    // ✅ Add new click handler
    map.on("click", async (e) => {
      // ✅ REMOVED the showKhasra check - this was causing the issue!
      // The handler should work if the khasraLayer exists
      if (!khasraLayer || !map) {
        console.log("❌ Khasra layer not available, ignoring click");
        return;
      }

      console.log("🖱️ Khasra click detected"); // Debug log

      const { lat, lng } = e.latlng;
      const layerName = khasraLayer.wmsParams.layers;

      // ✅ Use the same filter logic as in toggleKhasraLayer
      let cqlFilter = `state='${selectedState}' AND district='${selectedDistrict}' AND tehsil='${selectedTehsil}'`;

      // If village is selected and displayed (geoJsonLayer exists), filter for that village only
      if (villageName && geoJsonLayer) {
        cqlFilter += ` AND village='${villageName}'`;
      }

      console.log("🔍 Using CQL filter:", cqlFilter); // Debug log

      // ✅ Use correct point conversion
      const point = map.latLngToContainerPoint(e.latlng);
      const size = map.getSize();

      const wmsUrl =
        `${GEO_SERVER_BASE_URL}/wms?service=WMS&version=1.1.1&request=GetFeatureInfo` +
        `&layers=${layerName}&query_layers=${layerName}` +
        `&info_format=application/json` +
        `&x=${Math.floor(point.x)}&y=${Math.floor(point.y)}` +
        `&height=${size.y}&width=${size.x}` +
        `&srs=EPSG:4326&bbox=${map.getBounds().toBBoxString()}` +
        `&cql_filter=${encodeURIComponent(cqlFilter)}`;

      console.log("🌐 WMS URL:", wmsUrl); // Debug log

      try {
        const response = await fetch(wmsUrl);
        const data = await response.json();

        console.log("📊 WMS Response:", data); // Debug log

        if (!data.features || data.features.length === 0) {
          console.warn("❌ No feature returned from WMS click.");
          return;
        }

        const feature = data.features[0];
        const props = feature.properties;

        const tableFields = [
          { name: "clicked_lat", header: "Latitude" },
          { name: "clicked_lng", header: "Longitude" },
          { name: "state", header: "State" },
          { name: "district", header: "District" },
          { name: "tehsil", header: "Tehsil" },
          { name: "village", header: "Village" },
          { name: "khasra_no", header: "Khasra No" },
          { name: "area_ac", header: "Area (Ac)" },
          { name: "lu_lc", header: "LU/LC" },
        ];

        let tableHtml = `<table class="popup-table"><thead></thead><tbody>`;
        tableFields.forEach((f) => {
          let value =
            f.name === "clicked_lat"
              ? lat.toFixed(6)
              : f.name === "clicked_lng"
                ? lng.toFixed(6)
                : props[f.name] ?? "N/A";

          tableHtml += `<tr><td><strong>${f.header}</strong></td><td>${value}</td></tr>`;
        });

        // ✅ Add data source attribution to popup
        tableHtml += `<tr><td colspan="2" style="text-align: center; font-style: italic; color: #666; padding-top: 8px; border-top: 1px solid #ddd;">
          <small>Source: Land Revenue Department</small>
        </td></tr>`;

        tableHtml += `</tbody></table>`;

        // ✅ Remove previous highlight before adding new one
        if (khasraHighlightRef.current) {
          map.removeLayer(khasraHighlightRef.current);
          khasraHighlightRef.current = null;
        }

        const highlight = L.geoJSON(feature.geometry, {
          style: {
            color: "red",
            weight: 2,
            fillOpacity: 0.2,
          },
          onEachFeature: (f, layer) => {
            layer.bindPopup(tableHtml).openPopup();
          },
        }).addTo(map);

        khasraHighlightRef.current = highlight;
        map.fitBounds(highlight.getBounds());

        console.log("✅ Khasra feature highlighted");

      } catch (err) {
        console.error("Khasra click fetch failed:", err);
      }
    });

    console.log("✅ Khasra click handler setup complete");
  };

  const toggleKhasraLayer = async (
    selectedTehsil,
    selectedState,
    selectedDistrict,
    villageName
  ) => {
    try {
      setLoadingState(LOADING_STATES.KHASRA_LOADING);
      setProgress(20);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 20));

      if (!mapInstanceRef.current) {
        console.warn("Map not ready for Khasra toggle");
        setLoadingState(LOADING_STATES.IDLE);
        return;
      }

      if (!selectedTehsil || !selectedState || !selectedDistrict) {
        console.error("Missing input for Khasra layer filter.");
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');
        return;
      }

      if (showKhasra && khasraLayerRef.current) {
        // ✅ CLEANUP when hiding Khasra

        // 1. Remove the Khasra layer
        mapInstanceRef.current.removeLayer(khasraLayerRef.current);
        khasraLayerRef.current = null;

        // 2. Remove any existing highlight
        if (khasraHighlightRef.current) {
          mapInstanceRef.current.removeLayer(khasraHighlightRef.current);
          khasraHighlightRef.current = null;
        }

        // 3. Remove the click handler - IMPORTANT!
        mapInstanceRef.current.off("click");

        // 4. Update states
        setShowKhasra(false);
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');

        console.log("✅ Khasra layer hidden and click handler removed");
        return;
      }

      setProgress(40);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 40));

      let khasraLayerName = "";
      if (selectedState === "Rajasthan") {
        khasraLayerName = "ne:Rajasthan_Khasra_All1";
      } else if (selectedState === "Maharashtra") {
        khasraLayerName = "ne:MH_Khasra_All";
      } else if (selectedState === "Uttar Pradesh") {
        khasraLayerName = "ne:UP_Khasra_All";
      } else if (selectedState === "Andhra Pradesh") {
        khasraLayerName = "ne:AP_Khasra_All";
      } else if (selectedState === "Karnataka") {
        khasraLayerName = "ne:Karnataka_KHASRA_ALL";
      } else {
        alert("No Khasra layer configured for this state.");
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');
        return;
      }

      setProgress(60);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 60));

      // ✅ Use prettyName for WMS compatibility
      const displayState = prettyName(selectedState);
      const displayDistrict = prettyName(selectedDistrict);
      const displayTehsil = prettyName(selectedTehsil);

      let cqlFilter = `state='${displayState}' AND district='${displayDistrict}' AND tehsil='${displayTehsil}'`;

      // If village is selected and displayed (geoJsonLayer exists), filter for that village only
      if (villageName && geoJsonLayer) {
        const displayVillage = prettyName(villageName);
        cqlFilter += ` AND village='${displayVillage}'`;
        console.log(`🎯 Village-level Khasra filter: ${cqlFilter}`);
      } else {
        // If no village selected, show all Khasra for the tehsil
        console.log(`🏘️ Tehsil-level Khasra filter: ${cqlFilter}`);
      }

      if (!mapInstanceRef.current.getPane("khasraPane")) {
        mapInstanceRef.current.createPane("khasraPane");
        mapInstanceRef.current.getPane("khasraPane").style.zIndex = 450;
      }

      const khasraLayer = L.tileLayer.wms(
        `${GEO_SERVER_BASE_URL}/wms`,
        {
          layers: khasraLayerName,
          format: "image/png",
          transparent: true,
          version: "1.1.0",
          attribution: "Land Revenue Department",
          pane: "khasraPane",
          zIndex: 1000,
          CQL_FILTER: cqlFilter,
        }
      );

      // Add loading event handlers for Khasra
      khasraLayer.on('loading', () => {
        setProgress(80);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 80));
      });

      khasraLayer.on('load', () => {
        setProgress(100);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 100));

        setTimeout(() => {
          setLoadingState(LOADING_STATES.IDLE);
          setProgress(0);
          setLoadingMessage('');
        }, 400);
      });

      khasraLayer.on('tileerror', (error) => {
        console.error('Khasra layer loading error:', error);
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');

        if (!hasShownKhasraTileErrorRef.current) {
          alert('Failed to load Khasra layer. Please try again.');
          hasShownKhasraTileErrorRef.current = true;

          setTimeout(() => {
            hasShownKhasraTileErrorRef.current = false;
          }, 10000); // Reset after 10 seconds
        }
      });


      khasraLayer.addTo(mapInstanceRef.current);
      khasraLayerRef.current = khasraLayer;

      // ✅ Set showKhasra to true BEFORE setting up click handler
      setShowKhasra(true);

      // ✅ Setup click handler AFTER setting state
      setupKhasraClickHandler(
        mapInstanceRef.current,
        khasraLayer,
        villageName,
        selectedTehsil
      );

      console.log("✅ Khasra layer shown and click handler activated");
      setTimeout(() => ensureKhasraOnTop(), 100);

    } catch (err) {
      console.error("Failed to toggle Khasra:", err);
      setLoadingState(LOADING_STATES.IDLE);
      setProgress(0);
      setLoadingMessage('');
    }
  };

  const handleKhasraButtonClick = () => {
    // Get the village name from the currently displayed village layer
    let currentVillageName = selectedVillage ? selectedVillage : null;

    if (!currentVillageName && geoJsonLayer && geoJsonLayer.getLayers().length > 0) {
      // Extract village name from the displayed geoJsonLayer (fallback)
      const villageFeature = geoJsonLayer.getLayers()[0].feature;
      if (villageFeature && villageFeature.properties) {
        currentVillageName = villageFeature.properties.village ||
          villageFeature.properties.NAME_4 ||
          villageFeature.properties.NAME;
      }
    }

    console.log('🔍 Current village for Khasra:', currentVillageName);
    console.log('🗺️ geoJsonLayer exists:', !!geoJsonLayer);

    toggleKhasraLayer(
      selectedTehsil,
      selectedState,
      selectedDistrict,
      currentVillageName
    );
  };

  const ensureKhasraOnTop = () => {
    if (khasraLayerRef.current && showKhasra) {
      // Make sure our custom pane exists and has the highest z-index
      if (!mapInstanceRef.current.getPane("khasraPane")) {
        mapInstanceRef.current.createPane("khasraPane");
        mapInstanceRef.current.getPane("khasraPane").style.zIndex = 1000;
      }

      // Force the khasra layer to redraw in the highest pane
      mapInstanceRef.current.getPane("khasraPane").style.zIndex = 1000;
    }
  };

  const handleSatelliteToggle = () => {
    if (satelliteLayerRef.current) {
      if (isSatelliteVisible) {
        satelliteLayerRef.current.setOpacity(0);
      } else {
        satelliteLayerRef.current.setOpacity(1);
      }
      setIsSatelliteVisible(!isSatelliteVisible);
    }
  };

  // Check if any loading is active
  const isLoading = loadingState !== LOADING_STATES.IDLE;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="header">
        <h3>Crop Maps</h3>
      </div>

      {/* Map Container */}
      <div className="content-container">
        <div className="map-container">
          <div className="control-panel">
            <SelectionDropdown onSearch={handleSearch} />

            <button
              onClick={handleKhasraButtonClick} // ✅ Use the function instead of inline
              className={`modern-btn ${showKhasra ? 'toggle-active' : 'toggle-inactive'}`}
              disabled={!selectedState || !selectedTehsil || loadingState === LOADING_STATES.KHASRA_LOADING}
            >
              {loadingState === LOADING_STATES.KHASRA_LOADING
                ? "Loading..."
                : showKhasra
                  ? "Hide Khasra"
                  : "Show Khasra"
              }
            </button>

            <select
              value={selectedCrop || ""}
              onChange={handleCropChange}
              className="crop-dropdown"
              disabled={cropLayerLoading || loadingState === LOADING_STATES.CROP_LOADING}
            >
              <option value="">
                {loadingState === LOADING_STATES.CROP_LOADING ? "Loading..." : "Select Crop"}
              </option>
              {cropData.map((crop, index) => (
                <option key={index} value={crop}>
                  {crop}
                </option>
              ))}
            </select>

            <button
              onClick={handleSatelliteToggle}
              className={`modern-btn ${isSatelliteVisible ? 'toggle-active' : 'toggle-inactive'}`}
              disabled={isLoading}
            >
              {isSatelliteVisible ? "Hide Satellite" : "Show Satellite"}
            </button>
          </div>
          <div ref={mapRef} className="map-ref">
          </div>
        </div>
      </div>

      {/* Loading States */}
      {isLoading && (
        <>
          <Spinner />
          <ProgressBar
            loading={isLoading}
            progress={progress}
            message={loadingMessage}
          />
        </>
      )}
    </div>
  );
};

export default CropMappingView;
